import { Dispatch, SetStateAction } from 'react';
import { ReviewsExportProgress } from '@/context/reviews-page.context';
import { Sort } from '@/types/sort.type';
import { useExportLifecycle } from '@/components/action-buttons/use-export-lifecycle';
import { useReviewsCsvExportAction } from '@/components/action-buttons/use-reviews-csv-export-action';
import { useProductsCsvExportAction } from '@/components/action-buttons/use-products-csv-export-action';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

type UseExportActionsParams = {
    activeTable: ActionButtonsActiveTable;
    accessToken: string;
    filtersQuery: string[];
    sort: Sort;
    selectionSessionId: string;
    pickedReviews: string[];
    pickedProducts: string[];
    pickedBrandGroups: string[];
    isExportLoading: boolean;
    isSocketConnected: boolean;
    setExportProgress: Dispatch<SetStateAction<ReviewsExportProgress | null>>;
    scheduleExportProgressCleanup: (exportOperationId: string) => void;
};

type UseExportActionsReturn = {
    exportCsv: () => Promise<void>;
    exportProductsCsv: () => Promise<void>;
    cancelExport: () => void;
};

export const useExportActions = ({
    activeTable,
    accessToken,
    filtersQuery,
    sort,
    selectionSessionId,
    pickedReviews,
    pickedProducts,
    pickedBrandGroups,
    isExportLoading,
    isSocketConnected,
    setExportProgress,
    scheduleExportProgressCleanup,
}: UseExportActionsParams): UseExportActionsReturn => {
    const { startExport, finishExportSuccess, finishExportFailure, finishExportCanceled } = useExportLifecycle({
        isSocketConnected,
        setExportProgress,
        scheduleExportProgressCleanup,
    });
    const { exportCsv, cancelExport: cancelReviewsExport } = useReviewsCsvExportAction({
        accessToken,
        filtersQuery,
        sort,
        selectionSessionId,
        pickedReviews,
        isExportLoading,
        startExport,
        finishExportSuccess,
        finishExportFailure,
        finishExportCanceled,
    });
    const productsExportTable = activeTable === 'brands' ? 'brands' : 'products';
    const { exportProductsCsv, cancelExport: cancelProductsExport } = useProductsCsvExportAction({
        accessToken,
        activeTable: productsExportTable,
        selectionSessionId,
        pickedProducts,
        pickedBrandGroups,
        isExportLoading,
        startExport,
        finishExportSuccess,
        finishExportFailure,
        finishExportCanceled,
    });

    const cancelExport = () => {
        if (activeTable === 'reviews') {
            cancelReviewsExport();
            return;
        }

        cancelProductsExport();
    };

    return {
        exportCsv,
        exportProductsCsv,
        cancelExport,
    };
};
